import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { filter, finalize, Observable } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LiveUpdateEventTypes, growthMarketplaceTopic } from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { AinStateComponent } from '../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../shared/design-system/structured-json-display.component';
import { GrowthAdminService, GrowthAgentListingResponse, GrowthIndustryPackResponse } from './growth-admin.service';
import { growthAgentPermissionManifestFields } from './growth-agent-marketplace-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-agent-marketplace-listing-detail',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    RouterLink,
    FormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CheckboxModule,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page [title]="listing()?.displayName ?? t('growth.agentMarketplace.listingDetail.title')" [description]="t('growth.agentMarketplace.listingDetail.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.agentMarketplace.listingDetail.help.title'"
        [descriptionKey]="'growth.agentMarketplace.listingDetail.help.description'"
        [bulletKeys]="['growth.agentMarketplace.listingDetail.help.bullets.install', 'growth.agentMarketplace.listingDetail.help.bullets.killSwitch']" />

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (!listing()) {
        <ain-state type="empty" [title]="t('growth.agentMarketplace.listingDetail.messages.notFound')" />
      } @else {
        <div class="toolbar">
          <a class="back-link" [routerLink]="['/growth/marketplace/listings']">{{ t('catalog.action.backToList') }}</a>
          <div class="toolbar-actions">
            @if (canInstall()) {
              <button
                pButton
                type="button"
                [label]="t('growth.agentMarketplace.listings.install')"
                [loading]="actionType() === 'install'"
                (click)="install()"></button>
            }
            @if (canDisable()) {
              <button
                pButton
                type="button"
                severity="danger"
                [label]="t('growth.agentMarketplace.listings.disable')"
                [loading]="actionType() === 'disable'"
                (click)="disable()"></button>
            }
          </div>
        </div>

        <section class="card">
          <h3>{{ t('growth.agentMarketplace.listingDetail.sections.metadata') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.agentMarketplace.listingDetail.help.metadata.title'"
            [descriptionKey]="'growth.agentMarketplace.listingDetail.help.metadata.description'" />
          <div class="grid">
            <label>
              <span>{{ t('growth.agentMarketplace.listings.columns.key') }}</span>
              <input pInputText type="text" [ngModel]="listing()!.key" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.listings.columns.displayName') }}</span>
              <input pInputText type="text" [ngModel]="listing()!.displayName" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.listings.columns.version') }}</span>
              <input pInputText type="text" [ngModel]="listing()!.version" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.listingDetail.fields.agentDefinitionKey') }}</span>
              <input pInputText type="text" [ngModel]="listing()!.agentDefinitionKey" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.listings.columns.publisherType') }}</span>
              <input pInputText type="text" [ngModel]="listing()!.publisherType" [disabled]="true" />
            </label>
            <label>
              <span>{{ t('growth.agentMarketplace.listings.columns.installLifecycleStatus') }}</span>
              <input pInputText type="text" [ngModel]="lifecycleLabel(listing()!.installLifecycleStatus)" [disabled]="true" />
            </label>
          </div>
          @if (listing()!.description) {
            <label class="full">
              <span>{{ t('growth.agentMarketplace.listingDetail.fields.description') }}</span>
              <input pInputText type="text" [ngModel]="listing()!.description" [disabled]="true" />
            </label>
          }
        </section>

        @if (canInstall()) {
          <section class="card">
            <h3>{{ t('growth.agentMarketplace.listingDetail.sections.install') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.agentMarketplace.listingDetail.help.install.title'"
              [descriptionKey]="'growth.agentMarketplace.listingDetail.help.install.description'" />
            <ain-form-field [label]="t('growth.agentMarketplace.listingDetail.fields.industryPackKey')">
              <p-select
                [options]="industryPackOptions()"
                [(ngModel)]="selectedIndustryPackKey"
                optionLabel="label"
                optionValue="value"
                [showClear]="true"
                appendTo="body"
                [placeholder]="t('growth.agentMarketplace.listingDetail.fields.industryPackPlaceholder')" />
            </ain-form-field>
          </section>
        }

        <section class="card">
          <h3>{{ t('growth.agentMarketplace.listingDetail.sections.permissions') }}</h3>
          <ain-info-hint
            [titleKey]="'growth.agentMarketplace.permissions.help.title'"
            [descriptionKey]="'growth.agentMarketplace.permissions.help.description'" />
          <ain-structured-json-display [fields]="permissionFields" [payloadJson]="listing()!.permissionManifestJson" />
        </section>

        @if (listing()!.installId) {
          <section class="card">
            <h3>{{ t('growth.agentMarketplace.killSwitch.title') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.agentMarketplace.killSwitch.help.title'"
              [descriptionKey]="'growth.agentMarketplace.killSwitch.help.description'"
              [bulletKeys]="['growth.agentMarketplace.killSwitch.help.bullets.blockRun']" />
            <div class="kill-switch-row">
              <p-checkbox
                [binary]="true"
                [(ngModel)]="killSwitchEnabled"
                [inputId]="'listing-kill-switch'" />
              <label [for]="'listing-kill-switch'">{{ t('growth.agentMarketplace.killSwitch.fields.enabled') }}</label>
            </div>
            <ain-form-field [label]="t('growth.agentMarketplace.killSwitch.fields.reason')">
              <input pInputText type="text" [(ngModel)]="killSwitchReason" />
            </ain-form-field>
            <button
              pButton
              type="button"
              severity="danger"
              [label]="t('growth.agentMarketplace.killSwitch.actions.save')"
              [loading]="actionType() === 'killSwitch'"
              (click)="saveKillSwitch()"></button>
          </section>
        }

        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      }
    </ain-page>
  `,
  styles: [`
    .toolbar { margin-bottom: 12px; display: flex; justify-content: space-between; align-items: center; gap: 8px; flex-wrap: wrap; }
    .toolbar-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .back-link { color: var(--ain-color-primary); font-weight: 600; text-decoration: none; }
    .card { border: 1px solid #e5e7eb; border-radius: 12px; background: #fff; padding: 12px; margin-bottom: 12px; display: grid; gap: 10px; }
    .card h3 { margin: 0; }
    .grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
    label { display: grid; gap: 6px; }
    label.full { grid-column: 1 / -1; }
    .kill-switch-row { display: flex; align-items: center; gap: 8px; }
    .message.error { color: #b91c1c; }
    @media (max-width: 960px) { .grid { grid-template-columns: 1fr 1fr; } }
    @media (max-width: 640px) { .grid { grid-template-columns: 1fr; } }
  `]
})
export class GrowthAgentMarketplaceListingDetailComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly liveUpdateService = inject(LiveUpdateService);

  readonly loading = signal(true);
  readonly listing = signal<GrowthAgentListingResponse | null>(null);
  readonly errorMessage = signal('');
  readonly actionType = signal<'install' | 'disable' | 'killSwitch' | null>(null);
  readonly industryPackOptions = signal<Array<{ label: string; value: string }>>([]);

  readonly permissionFields = growthAgentPermissionManifestFields;

  selectedIndustryPackKey: string | null = null;
  killSwitchEnabled = false;
  killSwitchReason = '';

  private listingKey = '';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.listingKey = this.route.snapshot.paramMap.get('listingKey') ?? '';

    this.liveUpdateService.events$
      .pipe(
        filter(event => {
          if (event.eventType !== LiveUpdateEventTypes.growthMarketplaceChanged) {
            return false;
          }
          const payloadKey = event.payload['listingKey'];
          return payloadKey === undefined || String(payloadKey).toLowerCase() === this.listingKey.toLowerCase();
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => this.load(false));

    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.loadIndustryPacks();
    this.load(true);
  }

  lifecycleLabel(value: string | null | undefined): string {
    if (!value) {
      return '-';
    }
    return this.t(`growth.agentMarketplace.lifecycle.${value}`);
  }

  canInstall(): boolean {
    return !!this.listing()?.canInstall;
  }

  canDisable(): boolean {
    return this.listing()?.installLifecycleStatus === 'Installed' && !!this.listing()?.installId;
  }

  install(): void {
    this.runAction('install', () => this.api.installAgentMarketplaceListing(this.listingKey, this.selectedIndustryPackKey));
  }

  disable(): void {
    const installId = this.listing()?.installId;
    if (!installId) {
      return;
    }
    this.runAction('disable', () => this.api.disableAgentMarketplaceInstall(installId));
  }

  saveKillSwitch(): void {
    const installId = this.listing()?.installId;
    if (!installId) {
      return;
    }
    this.runAction('killSwitch', () => this.api.setAgentMarketplaceInstallKillSwitch(
      installId,
      this.killSwitchEnabled,
      this.killSwitchReason.trim() || null
    ));
  }

  load(showLoading = true): void {
    if (!this.listingKey) {
      this.loading.set(false);
      this.listing.set(null);
      return;
    }
    if (showLoading) {
      this.loading.set(true);
    }
    this.errorMessage.set('');
    this.api.getAgentMarketplaceListing(this.listingKey).subscribe({
      next: row => {
        this.listing.set(row);
        this.killSwitchEnabled = row.killSwitchEnabled;
        this.loading.set(false);
      },
      error: () => {
        this.listing.set(null);
        this.loading.set(false);
        this.errorMessage.set(this.t('growth.agentMarketplace.listingDetail.messages.loadFailed'));
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private loadIndustryPacks(): void {
    this.api.listIndustryPacks().subscribe({
      next: packs => {
        this.industryPackOptions.set(
          packs
            .filter((pack: GrowthIndustryPackResponse) => pack.lifecycleStatus === 'Installed')
            .map(pack => ({ label: `${pack.displayName} (${pack.key})`, value: pack.key }))
        );
      }
    });
  }

  private runAction(
    type: 'install' | 'disable' | 'killSwitch',
    request: () => Observable<unknown>
  ): void {
    this.actionType.set(type);
    this.errorMessage.set('');
    request().pipe(finalize(() => this.actionType.set(null))).subscribe({
      next: () => this.load(false),
      error: () => this.errorMessage.set(this.t(`growth.agentMarketplace.listingDetail.messages.${type}Failed`))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthMarketplaceTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
